import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { icons } from '@tsq-web/assets';
import { TSqValidators } from '@tsq-web/forms';

@Component({
  selector: 'comm-neighbor-mass-invite-emails-input-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      class="hover:bg-petro-n1 text-new-primary-p2 focus:outline-new-primary mb-16 rounded transition-colors focus:outline focus:outline-offset-2"
      (click)="goBack.emit()"
    >
      <tsq-icon [icon]="arrowLeftIcon" />
    </button>

    <h1>{{ 'MASS_INVITE_NEIGHBORS.SEND_INVITES' | translate }}</h1>

    <p class="text-md mt-8 leading-normal">
      {{ 'MASS_INVITE_NEIGHBORS.SEND_INVITES_DESCRIPTION' | translate }}
    </p>

    <div class="mt-16 py-8">
      <span *ngIf="helperMessage$ | async as helperMessage" class="block px-16 pb-8">
        {{ helperMessage }}
      </span>
      <form
        class="border-1 border-petro-n2 items-top focus-within:outline-new-primary flex min-h-[50px] w-full flex-wrap rounded px-16 pt-8 focus-within:outline focus-within:outline-offset-2"
        autocomplete="off"
        [formGroup]="form"
      >
        <tsq-chips-list
          class="display-contents input-chips"
          schema="new-primary"
          [options]="emailChips$ | async"
          [hasCheck]="false"
          [hasRemove]="true"
          [allSelected]="true"
          [invalidMultiple]="invalidChipsIndexes$ | async"
          (optionRemoved)="removeChip($event)"
        />
        <input
          class="font-base mb-12 mt-4 block min-w-[200px] grow focus:outline-none"
          autocomplete="off"
          formControlName="emailInput"
          [placeholder]="'MASS_INVITE_NEIGHBORS.EMAIL_PLACEHOLDER_V2' | translate"
          (keydown)="onKeyDown($event)"
          (paste)="onPaste($event)"
          (blur)="onBlur()"
        />
      </form>
    </div>

    <div class="mt-16 flex w-full flex-col justify-center">
      <tsq-button
        *ngIf="!isSending; else loader"
        schema="new-primary"
        size="lg"
        [text]="'MASS_INVITE_NEIGHBORS.SEND' | translate"
        [disabled]="form.invalid"
        (clicked)="submitEmails()"
      />

      <ng-template #loader>
        <tsq-small-loader />
      </ng-template>
    </div>
  `,
})
export class EmailsInputCardComponent {
  @Input() isSending = false;

  @Output() goBack = new EventEmitter<void>();
  @Output() sendInvites = new EventEmitter<string[]>();

  private readonly translateService = inject(TranslateService);

  protected readonly form = new FormGroup({
    emailInput: new FormControl(''),
    chips: new FormArray<FormControl<string>>([], Validators.required),
  });
  protected readonly emailChips$ = this.form.controls.chips.valueChanges.pipe(
    map(chipArr => chipArr.map(chip => chip)),
  );
  protected readonly invalidChipsIndexes$ = this.form.controls.chips.valueChanges.pipe(
    map(() =>
      this.form.controls.chips.controls.reduce((acc: number[], control, index) => {
        if (control.invalid) {
          acc.push(index);
        }
        return acc;
      }, []),
    ),
  );

  protected readonly helperMessage$ = this.form.controls.chips.valueChanges.pipe(
    map(emailsArr => {
      const chipsControl = this.form.controls.chips;
      const emailsAmount = emailsArr.length;

      if (!chipsControl.length) {
        return '';
      }

      if (chipsControl.invalid) {
        const invalidChipsAmount = chipsControl.controls.filter(control => control.invalid).length;

        return this.translateService.instant(
          `MASS_INVITE_NEIGHBORS.EMAIL_ISSUE_LABEL${emailsAmount === 1 ? '' : '_P'}${
            invalidChipsAmount === 1 ? '' : '_P'
          }`,
          { emails: emailsAmount, invalidEmails: invalidChipsAmount },
        );
      }

      return this.translateService.instant(
        `MASS_INVITE_NEIGHBORS.EMAIL_LABEL${emailsAmount === 1 ? '' : '_P'}`,
        { emails: emailsAmount },
      );
    }),
  );

  protected readonly arrowLeftIcon = icons.arrowLeft;

  private readonly submissionKeys = ['Enter', ' '];
  private readonly separatorKeys = [' ', ',', ';', '\n'];

  onKeyDown(event: KeyboardEvent): void {
    const emailInput = this.form.controls.emailInput;
    const submissionAndSeparatorKeys = [...this.submissionKeys, ...this.separatorKeys];

    if (submissionAndSeparatorKeys.some(key => key === event.key) && !!emailInput.value?.trim()) {
      event.preventDefault();

      this.pushEmailControl(emailInput.value);
      this.clearInput();
    }

    if (event.key === 'Backspace' && !emailInput.value) {
      if (!event.repeat) {
        this.form.controls.chips.removeAt(this.form.controls.chips.length - 1);
      }
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.stopPropagation();
    event.preventDefault();

    const pastedText = event.clipboardData.getData('text');
    const splittedEmails = pastedText.split(new RegExp(this.separatorKeys.join('|').toString()));

    splittedEmails.forEach(email => this.pushEmailControl(email));
  }

  onBlur(): void {
    this.pushEmailControl(this.form.controls.emailInput.value);
    this.clearInput();
  }

  removeChip(chipIndex: number): void {
    this.form.controls.chips.removeAt(chipIndex);
  }

  submitEmails(): void {
    const emails = this.form.controls.chips.value;
    this.sendInvites.emit(emails);
  }

  private pushEmailControl(controlValue: string): void {
    if (!controlValue?.trim()) {
      return;
    }

    this.form.controls.chips.push(
      new FormControl(controlValue?.trim(), [TSqValidators.validEmail]),
    );
  }

  private clearInput(): void {
    this.form.controls.emailInput.reset();
  }
}
